<script setup>
import CertificatesTable from "./certificates/CertificatesTable.vue";
import CertificateDetails from "./certificates/CertificateDetails.vue";
import { computed, onMounted, ref, watch } from "vue";
import { useAsyncState } from "@vueuse/core";
import { useRoute } from "vue-router";
import PanelSearch from "@/apps/player/panel/PanelSearch.vue";
import { issuedCertificatesService } from "@/apps/player/services/issued-certificates.service";
import { playerCertificatesService } from "@/apps/player/services/player-certificates.service";
import { NotificationAlert } from "@easy/ui/notifications";
import { usePagination } from "@/shared/utils/usePagination";
import CertificateFilters from "@/apps/player/panel/certificates/CertificateFilters.vue";

const { params } = useRoute();
const playerUUID = computed(() => params.projectUuid);
const showDetails = ref(false);
const areDetailsLoading = ref(false);
const certificateToShow = ref({});

const selectCertificate = async (certificate) => {
    certificateToShow.value = certificate;
    showDetails.value = true;
};

const { state: certTemplate, isLoading: certTemplateLoading } = useAsyncState(
    () => playerCertificatesService.getCertificateTemplate(playerUUID.value),
    {},
    { immediate: true }
);
// on purpose certTemplateLoading.value condition below to not have "bumping message" on 1st render
const hasCertTemplate = computed(
    () => certTemplateLoading.value || Boolean(certTemplate.value?.id)
);
const hasCertificationActive = computed(
    () => certTemplateLoading.value || Boolean(certTemplate.value?.isActive)
);
const { updatePaginationUnwrapResult, page, pageSize, totalPages } = usePagination({
    size: 100,
    fetchAction: () => reloadIssuedCerts(),
});
const search = ref("");
const {
    state: issuedCertificatesList,
    execute: reloadIssuedCerts,
    isLoading,
} = useAsyncState(
    () =>
        issuedCertificatesService
            .getAllFor(playerUUID.value, {
                search: search.value,
                page: page.value,
                size: pageSize.value,
            })
            .then(updatePaginationUnwrapResult),
    [],
    {
        immediate: false,
    }
);

async function regenerateCert(certificate) {
    areDetailsLoading.value = true;
    const updatedCertData = await issuedCertificatesService.regenerate(
        playerUUID.value,
        certificate.uuid
    );
    // todo - some confirmation message
    await reloadIssuedCerts();
    certificateToShow.value = { ...certificateToShow.value, ...updatedCertData };
    areDetailsLoading.value = false;
}

onMounted(async () => reloadIssuedCerts());
watch([search], (value, oldValue) => {
    if (value !== oldValue) {
        reloadIssuedCerts();
    }
});
</script>

<template>
    <div class="p-4" v-if="!hasCertTemplate">
        <NotificationAlert :closeButton="false">
            <p>
                {{ $t("player.panel.certificates.not_active") }}
                <router-link :to="{ name: PLAYER_ROUTE_NAMES.projectCertificates }">
                    <span class="underline">
                        {{ $t("player.panel.certificates.not_active_link") }}
                    </span>
                </router-link>
            </p>
        </NotificationAlert>
    </div>
    <template v-else>
        <div class="p-4" v-if="!hasCertificationActive">
            <NotificationAlert :closeButton="false">
                <p>
                    {{ $t("player.panel.certificates.not_currently_active") }}
                    <router-link :to="{ name: PLAYER_ROUTE_NAMES.projectCertificates }">
                        <span class="underline">
                            {{ $t("player.panel.certificates.not_active_link") }}
                        </span>
                    </router-link>
                </p>
            </NotificationAlert>
        </div>

        <CertificateFilters v-model:pageSize="pageSize" />

        <PanelSearch
            v-model="search"
            :placeholder="$t('player.panel.certificates.search')"
            :linkToPlayer="`/players/${playerUUID}`"
        />

        <CertificatesTable
            v-model:currentPage="page"
            :certs="issuedCertificatesList"
            :isLoading="isLoading"
            :totalPages="totalPages"
            :searchQuery="search"
            @select="selectCertificate"
        />
        <CertificateDetails
            v-if="showDetails"
            @hide="showDetails = false"
            @regenerate="regenerateCert"
            :certificate="certificateToShow"
            :isLoading="areDetailsLoading"
        />
    </template>
</template>
